import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import type { LineClampVariantProps } from '@/hen-ui/atomics/LineClamp/line-clamp.variants';
import {
  ClampTriggerContextProvider,
  ClampTriggerRegistrationProvider,
  DEFAULT_CLAMP_COLLAPSED_LABEL,
  DEFAULT_CLAMP_EXPANDED_LABEL,
  useIsomorphicLayoutEffect,
} from './clampTriggerContext';

export {
  DEFAULT_CLAMP_COLLAPSED_LABEL,
  DEFAULT_CLAMP_EXPANDED_LABEL,
  resolveClampContentIds,
  useClampTriggerContext,
} from './clampTriggerContext';

export interface ClampControlProps {
  trigger?: React.ReactElement;
  collapsedLabel?: string;
  expandedLabel?: string;
  /** When false, content is visually clamped with no expand/collapse control. */
  expandable?: boolean;
}

interface UseClampControlOptions<TElement extends HTMLElement> {
  lines: LineClampVariantProps['lines'];
  content: React.ReactNode;
  forwardedRef: React.ForwardedRef<TElement>;
  expandable?: boolean;
  /**
   * Element inside the clamp host that actually holds the line boxes.
   * A block-level child, such as Text's paragraph, is not clamped by
   * `line-clamp` on the wrapper, so overflow has to be measured there.
   */
  measureSelector?: string;
}

const setForwardedRef = <TElement,>(
  ref: React.ForwardedRef<TElement>,
  value: TElement | null,
): void => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};

export const useClampControl = <TElement extends HTMLElement>({
  lines,
  content,
  forwardedRef,
  expandable = false,
  measureSelector,
}: UseClampControlOptions<TElement>) => {
  const contentId = React.useId();
  const isExpandableClamp = expandable && lines !== 'none' && lines != null;
  const [element, setElement] = React.useState<TElement | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const expandedRef = React.useRef(expanded);
  const previousTextRef = React.useRef<string | null>(null);
  expandedRef.current = expanded;

  const contentRef = React.useCallback(
    (node: TElement | null) => {
      setElement(isExpandableClamp ? node : null);
      setForwardedRef(forwardedRef, node);
    },
    [forwardedRef, isExpandableClamp],
  );

  const measure = React.useCallback(() => {
    if (!element || !isExpandableClamp || expandedRef.current) return;
    const nested = measureSelector
      ? Array.from(element.querySelectorAll<HTMLElement>(measureSelector))
      : [];
    const targets = [element, ...nested];
    setHasOverflow(
      targets.some((node) => node.scrollHeight > node.clientHeight),
    );
  }, [element, isExpandableClamp, measureSelector]);

  // Reset expansion only when the rendered text actually changes — not when a
  // parent rerender creates a new ReactNode identity for identical content.
  useIsomorphicLayoutEffect(() => {
    if (!isExpandableClamp) {
      setExpanded(false);
      setHasOverflow(false);
      previousTextRef.current = null;
      return;
    }

    const text = element?.textContent ?? null;
    if (
      text !== null &&
      previousTextRef.current !== null &&
      previousTextRef.current !== text
    ) {
      setExpanded(false);
    }
    if (text !== null) {
      previousTextRef.current = text;
    }
  }, [content, element, isExpandableClamp]);

  useIsomorphicLayoutEffect(() => {
    measure();
  }, [content, expandable, lines, measure, expanded]);

  React.useEffect(() => {
    if (!element || !isExpandableClamp || typeof ResizeObserver === 'undefined')
      return;

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (measureSelector) {
      element
        .querySelectorAll(measureSelector)
        .forEach((node) => observer.observe(node));
    }
    return () => observer.disconnect();
  }, [element, isExpandableClamp, measure, measureSelector]);

  return {
    contentId,
    contentRef,
    expanded,
    showTrigger: isExpandableClamp && (hasOverflow || expanded),
    toggleExpanded: React.useCallback(() => {
      setExpanded((current) => !current);
    }, []),
  };
};

interface ClampTriggerProps extends ClampControlProps {
  contentId: string;
  expanded: boolean;
  onToggle: () => void;
}

export const ClampTrigger = ({
  trigger,
  collapsedLabel = DEFAULT_CLAMP_COLLAPSED_LABEL,
  expandedLabel = DEFAULT_CLAMP_EXPANDED_LABEL,
  contentId,
  expanded,
  onToggle,
}: ClampTriggerProps) => {
  const label = expanded ? expandedLabel : collapsedLabel;
  const value = React.useMemo(
    () => ({ label, expanded, contentId, onToggle }),
    [contentId, expanded, label, onToggle],
  );
  const consumerCount = React.useRef(0);
  const registration = React.useMemo(
    () => ({
      register: () => {
        consumerCount.current += 1;
      },
      unregister: () => {
        consumerCount.current = Math.max(0, consumerCount.current - 1);
      },
    }),
    [],
  );
  const [missingConsumer, setMissingConsumer] = React.useState(!trigger);

  useIsomorphicLayoutEffect(() => {
    setMissingConsumer(!trigger || consumerCount.current === 0);
  });

  const fallback = (
    <ClampTriggerContextProvider value={value}>
      <Button variant='textOnly' />
    </ClampTriggerContextProvider>
  );

  if (!trigger) return fallback;

  return (
    <>
      <ClampTriggerContextProvider value={value}>
        <ClampTriggerRegistrationProvider value={registration}>
          {trigger}
        </ClampTriggerRegistrationProvider>
      </ClampTriggerContextProvider>
      {missingConsumer ? fallback : null}
    </>
  );
};
